import { createContext, FC, useCallback, useContext, useMemo } from "react";

import {
  SchemaItemProps,
  useAppContext,
  useAsyncCallback,
  useClientContext,
  useField,
  useNavigation,
  UseRowContext,
} from "@data-root/react";
import {
  EnumOption,
  RefSchemaItem,
  RowWithEmbeddedMetadata,
  Table,
} from "@data-root/types";
import { inputGuard } from "../../../hocs/guard";
import { RefInputCarouselPresentation } from "./CarouselPresentation";
import { RefInputEmbeddedPresentation } from "./EmbeddedPresentation";
import { RefInputFormPresentation } from "./FormPresentation";
import { RefInputModalPresentation } from "./ModalPresentation";
import { RefInputInlinePresentation } from "./InlinePresentation";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { App as AntdApp } from "antd";
import { IRefInputContext, RefInputPresentation } from "./types";

export type { IRefInputContext };

export const RefInputContext = createContext<IRefInputContext | null>(null);

export const RefInput: FC<SchemaItemProps<RefSchemaItem>> = inputGuard(
  (props) => {
    const antdApp = AntdApp.useApp();

    const { item } = props;

    const queryClient = useQueryClient();

    const { core } = useClientContext();
    const { appSpecifier, appId, getTable } = useAppContext();
    const { navigation } = useNavigation();

    const { rowId, commit, dispatch, dispatchAndCommit } =
      useContext(UseRowContext)!;

    const { loaded, value, editable, show, getExtendedContext } =
      useField(item);

    const refTable = useMemo(() => getTable(item.ref), [getTable, item.ref]);

    const multiple = useMemo(() => item.multiple || false, [item.multiple]);

    const presentation = (item.presentation || "Modal") as RefInputPresentation;

    const isNullish = useMemo<boolean>(() => {
      // NOTE: We need to check for null and undefined because of the way we store
      if (!loaded) return true;
      return value === null || !value;
    }, [loaded, value]);

    const values = useMemo<string[]>(() => {
      if (isNullish) return [];
      return (multiple ? value : [value]) as string[];
    }, [isNullish, multiple, value]);

    const { rows, ready } = useQueries({
      queries: values.map((rowId) => ({
        queryKey: ["row", appSpecifier, item.ref, rowId],
        queryFn: async () => await core.get(appSpecifier, item.ref, rowId),
      })),
      combine: (results) => ({
        rows: results.map((result) => result.data),
        ready: results.every((result) => result.status === "success"),
      }),
    });

    const { data: pinned } = useQuery({
      queryKey: ["pinned", appSpecifier, item.ref, , item.pinned],
      queryFn: async () => {
        if (!item.pinned) return [];

        const results = await Promise.allSettled(
          item.pinned.map((rowId) => core.get(appSpecifier, item.ref, rowId))
        );

        results.forEach((result) => {
          if (result.status === "rejected")
            console.warn("Failed to fetch pinned rows");
        });

        const rows = results
          .filter((result) => result.status === "fulfilled")
          .map(
            (result) =>
              (result as PromiseFulfilledResult<RowWithEmbeddedMetadata>).value
          );

        const options: EnumOption[] = rows.map(
          (row) =>
            ({
              id: row.id,
              name: row[`$${refTable.label}`],
            } as EnumOption)
        );

        return options;
      },
      placeholderData: [],
      enabled: !!item.pinned,
    });

    const invalidate = useCallback(async () => {
      await queryClient.invalidateQueries({
        predicate(query) {
          return (
            query.queryKey.slice(0, 3).join("/") ===
              `row/${appId}/${item.ref}` &&
            values.includes(query.queryKey[3] as string)
          );
        },
      });
    }, [appId, item.ref, queryClient, values]);

    const onAdd = useAsyncCallback(async () => {
      if (!loaded) return;
      if (!editable) return;

      try {
        await commit();
      } catch (error) {
        antdApp.modal.error({
          title: "Error al guardar",
          content: "Ocurrió un error al guardar los datos",
          centered: true,
          okText: "Aceptar",
        });
        return;
      }

      const inherit = { ...item.inherit };

      // TODO: This is only accounting for bidirectional refs (A <-> B) where B is single
      if (item.bidirectional) inherit[item.refField!] = rowId;

      const context = getExtendedContext();

      const refRow = await core.emptyRow(
        appSpecifier,
        item.ref,
        undefined,
        undefined,
        inherit,
        context
      );

      const { id: deltaId } = await core.add(appSpecifier, item.ref, refRow);

      try {
        await dispatchAndCommit(
          multiple
            ? {
                type: "ArrayPush",
                payload: {
                  fieldId: item.id,
                  value: refRow.id,
                  globalDisplayValue: refRow.id,
                },
              }
            : {
                type: "Set",
                payload: {
                  fieldId: item.id,
                  value: refRow.id,
                  displayValue: refRow.id,
                },
              }
        );
      } catch (error) {
        antdApp.modal.error({
          title: "Error al guardar",
          content: "Ocurrió un error al guardar los datos",
          centered: true,
          okText: "Aceptar",
        });
        return;
      }

      const appNavigation = navigation.getParent("app");
      appNavigation.setParams({ rowId });
      appNavigation.navigate(item.formViewId!, {
        rowId: refRow.id,
        deltaId,

        parentTableId: context.tableId!,
        parentRowId: rowId,
      });
    }, [
      loaded,
      editable,
      item.inherit,
      item.bidirectional,
      item.refField,
      item.ref,
      item.formViewId,
      item.id,
      rowId,
      getExtendedContext,
      core,
      appSpecifier,
      navigation,
      commit,
      antdApp.modal,
      dispatchAndCommit,
      multiple,
    ]);

    const onEdit = useAsyncCallback(
      async (refRow: RowWithEmbeddedMetadata) => {
        if (!loaded) return;
        if (!editable) return;

        try {
          await commit();
        } catch (error) {
          antdApp.modal.error({
            title: "Error al guardar",
            content: "Ocurrió un error al guardar los datos",
            centered: true,
            okText: "Aceptar",
          });
          return;
        }

        const appNavigation = navigation.getParent("app" as any)!;
        appNavigation.setParams({ rowId } as any);
        appNavigation.navigate(item.formViewId!, {
          rowId: refRow.id,
        });
      },
      [
        loaded,
        editable,
        navigation,
        rowId,
        item.formViewId,
        commit,
        antdApp.modal,
      ]
    );

    const onDelete = useAsyncCallback(
      async (refRow: RowWithEmbeddedMetadata) => {
        if (!loaded) return;
        if (!editable) return;

        const index = values.indexOf(refRow.id);
        const newValues = [...values];
        newValues.splice(index, 1);

        // ! TODO: use mutation instead
        const rowResults = await Promise.allSettled(
          newValues.map((rowId) => core.get(appSpecifier, item.ref, rowId))
        );

        if (rowResults.some((result) => result.status === "rejected"))
          throw new Error("Failed to fetch ref rows");

        const globalDisplayValue = rowResults
          .map(
            (result) =>
              (result as PromiseFulfilledResult<RowWithEmbeddedMetadata>).value
          )
          .map((row) => row[refTable.label])
          .join(", ");

        dispatch({
          type: "ArrayPop",
          payload: {
            fieldId: item.id,
            index,
            globalDisplayValue,
          },
        });
      },
      [
        loaded,
        editable,
        values,
        dispatch,
        item.id,
        item.ref,
        core,
        appSpecifier,
        refTable.label,
      ]
    );

    const Presentation = useMemo(() => {
      switch (presentation) {
        case "Carousel":
          return <RefInputCarouselPresentation {...props} />;
        case "Form":
        case "Deck":
          return <RefInputFormPresentation {...props} />;
        case "Embedded":
          return <RefInputEmbeddedPresentation {...props} />;
        case "Inline":
          return <RefInputInlinePresentation {...props} />;
        case "Modal":
        default:
          return <RefInputModalPresentation {...props} />;
      }
    }, [presentation, props]);

    const refContext = useMemo(
      () => ({
        ready,
        onAdd,
        onEdit,
        onDelete,
        multiple,
        rows,
        refTable,
        invalidate,
        pinned,
      }),
      [
        invalidate,
        multiple,
        onAdd,
        onDelete,
        onEdit,
        pinned,
        ready,
        refTable,
        rows,
      ]
    );

    if (!show) return null;

    return (
      <RefInputContext.Provider value={refContext}>
        {Presentation}
      </RefInputContext.Provider>
    );
  }
);
